import { getKioskEmployeeId } from "@wkt/lib/auth-context";
import { defineStore } from "pinia";

interface ShellState {
  activeRouteName: string | null;
  currentBridgeModule: string | null;
  activeShellMode: "full" | "none";
  globalShellHostEnabled: boolean;
  badges: Record<string, number>;
  sidebarOpen: boolean;
  isMobile: boolean;
  activeKioskEmployeeId: number | null;
  kioskTimeout: number;
  kioskTimeoutMax: number;
  pendingRevealModule: string | null;
  pendingRevealPayload: unknown;
  pendingRevealToken: number;
  refreshToken: number;
}

function readKioskEmployeeId(): number | null {
  const rawValue = getKioskEmployeeId();
  if (!rawValue) {
    return null;
  }

  const parsed = Number.parseInt(rawValue, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export const useShellStore = defineStore("shell", {
  state: (): ShellState => ({
    activeRouteName: null,
    currentBridgeModule: null,
    activeShellMode: "none",
    globalShellHostEnabled: false,
    badges: {
      home: 0,
      checkin: 0,
      procedures: 0,
      documents: 0,
      expensesheets: 0,
      training: 0,
      news: 0,
    },
    sidebarOpen: true,
    isMobile: typeof window !== "undefined" ? window.innerWidth < 992 : false,
    activeKioskEmployeeId: null,
    kioskTimeout: 21,
    kioskTimeoutMax: 21,
    pendingRevealModule: null,
    pendingRevealPayload: null,
    pendingRevealToken: 0,
    refreshToken: 0,
  }),
  getters: {
    isKioskSession: (state) => state.activeKioskEmployeeId !== null,
    totalBadges: (state) =>
      Object.values(state.badges).reduce((total, count) => total + count, 0),
    appIconBadgeTotal: (state) =>
      (state.badges.home ?? 0) +
      (state.badges.checkin ?? 0) +
      (state.badges.procedures ?? 0) +
      (state.badges.documents ?? 0) +
      (state.badges.expensesheets ?? 0) +
      (state.badges.training ?? 0),
  },
  actions: {
    setActiveRouteName(routeName: string | null) {
      this.activeRouteName = routeName;
    },
    setCurrentBridgeModule(moduleName: string | null) {
      this.currentBridgeModule = moduleName;
    },
    setActiveShellMode(shellMode: "full" | "none") {
      this.activeShellMode = shellMode;
    },
    setGlobalShellHostEnabled(enabled: boolean) {
      this.globalShellHostEnabled = enabled;
    },
    setBadge(name: string, count: number) {
      this.badges[name] = Math.max(0, count || 0);
    },
    setBadges(nextBadges: Partial<Record<string, number>>) {
      Object.entries(nextBadges).forEach(([name, count]) => {
        this.setBadge(name, count ?? 0);
      });
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    setSidebarOpen(nextValue: boolean) {
      this.sidebarOpen = nextValue;
    },
    updateViewport(width: number) {
      this.isMobile = width < 992;
      if (this.isMobile) {
        this.sidebarOpen = false;
      }
    },
    setActiveKioskEmployee(employeeId: number | null) {
      this.activeKioskEmployeeId = employeeId;
    },
    resetKioskTimeout() {
      this.kioskTimeout = this.kioskTimeoutMax;
    },
    decrementKioskTimeout() {
      this.kioskTimeout -= 1;
    },
    syncKioskFromStorage() {
      this.activeKioskEmployeeId = readKioskEmployeeId();
    },
    queueReveal(moduleName: string, payload?: unknown) {
      this.pendingRevealModule = moduleName;
      this.pendingRevealPayload = payload ?? null;
      this.pendingRevealToken += 1;
      if (moduleName === "KioskModule") {
        this.activeKioskEmployeeId = null;
      } else {
        this.syncKioskFromStorage();
      }
    },
    clearPendingReveal(moduleName?: string | null) {
      if (
        moduleName &&
        this.pendingRevealModule &&
        this.pendingRevealModule !== moduleName
      ) {
        return;
      }

      this.pendingRevealModule = null;
      this.pendingRevealPayload = null;
    },
    requestRefresh() {
      this.refreshToken += 1;
    },
  },
});
